/**
 * IMPORT orders must carry a weight.
 *
 * One rule, consumed by the drawer's save validator (useOrderEditing), the
 * scheduler's Finalize modal / LC Finalize Prepull pre-check, and the grids'
 * "NO WT" flag. Mirrors backend/app/services/import_weight_rule.py: `lbs` is
 * the authoritative weight (kgs / metric tons cascade into it), and NULL,
 * blank, non-numeric or 0 all count as missing — v1 stores 0 for "unknown".
 *
 * Design: docs/plans/2026-09-01-import-weight-required-design.md
 */

export const IMPORT_WEIGHT_REQUIRED = 'Weight (Lbs) is required on IMPORT orders.';

export const IMPORT_WEIGHT_REQUIRED_NO_ACCESS =
  `${IMPORT_WEIGHT_REQUIRED} You don't have access to edit weight. ` +
  "Contact the order's Logistics Coordinator (LC) to update it.";

export const SCHEDULING_MISSING_WEIGHT_TOOLTIP =
  'Missing Weight. Required on IMPORT orders; Finalize is blocked until it is entered.';

/** NULL / blank / non-numeric / 0 all count as missing. */
export function isWeightMissing(value: unknown): boolean {
  if (value == null) return true;
  const s = String(value).trim().replace(/,/g, '');
  if (s === '') return true;
  const n = Number(s);
  return !Number.isFinite(n) || n === 0;
}

/** True only for IMPORT move types with a missing weight. */
export function isImportMissingWeight(moveType: unknown, lbs: unknown): boolean {
  return String(moveType ?? '').trim().toUpperCase() === 'IMPORT' && isWeightMissing(lbs);
}

/**
 * The save-banner message. `canEditWeightFields === false` (no
 * `order-weight-fields` grant) gets the "contact the LC" variant because that
 * user cannot fix it inline; unknown/true gets the short form.
 */
export function importWeightRequiredMessage(canEditWeightFields: boolean | undefined): string {
  return canEditWeightFields === false ? IMPORT_WEIGHT_REQUIRED_NO_ACCESS : IMPORT_WEIGHT_REQUIRED;
}

/**
 * Scheduler rows: the backend only sets `is_missing_weight` for IMPORT PROs,
 * but re-check the move type here so a stale payload can never block a
 * non-IMPORT finalize.
 */
export function legBlockedForMissingWeight(leg: {
  move_type?: string | null;
  is_missing_weight?: boolean;
}): boolean {
  return leg.move_type === 'IMPORT' && !!leg.is_missing_weight;
}
